export default {
   baseUrl: `${import.meta.env.VITE_BASE_API_URL}/v1`,

   auth: {
      signOut: 'auth/signout',
   },

   user: {
      getCurrentUser: 'users/me',
      getUsers: 'site-users',
      setRoles: 'users/SetRoles',
      siteUsers: 'site-users',
      userEmail: 'users/email',
      userProfile: 'users/profile',
      users: 'users',
      cards: 'users/me/cards',
      // NPT-2083 — Help Scout Beacon identify payload (Secure Mode signature).
      supportIdentity: 'users/me/support-identity',
      hostedProfilePageToken: 'users/hosted-profile-page-token',
      hostedCardEditPageToken: 'users/card/:cardId/hosted-edit-page-token',
      syncCards: 'users/sync-cards',
      setDefaultCard: 'users/card/:cardId/default',
      deleteCard: 'users/card/:cardId/delete',
      updateCard: 'users/card/:cardId',
      // NPT-2091 — Platform Admin creates a traveler at checkout; public claim-link lookup.
      travelers: 'site-users/travelers',
      travelerClaim: 'traveler-claims/:token',
   },

   role: {
      Roles: 'roles',
      adminRoles: 'roles/adminRoles',
      organizationRoles: 'roles/organizationRoles',
   },

   organization: {
      filter: 'organizations/Filter',
      organizations: 'organizations',
      travelerOrganization: 'traveler/organizations',
   },

   // NPT-2144: read-only org admin surface. `:organizationId` is always the admin's context org.
   orgAdmin: {
      scope: 'organizations/:organizationId/org-admin/scope',
      trips: 'organizations/:organizationId/org-admin/trips',
      tripsByEvent: 'organizations/:organizationId/org-admin/trips/by-event',
      tripsExport: 'organizations/:organizationId/org-admin/trips/export',
      users: 'organizations/:organizationId/org-admin/users',
      scheduleChangeRequests: 'organizations/:organizationId/org-admin/schedule-change-requests',
      proceedsReport: 'organizations/:organizationId/org-admin/proceeds-report',
      proceedsReportTeams: 'organizations/:organizationId/org-admin/proceeds-report/teams',
      proceedsReportBookings: 'organizations/:organizationId/org-admin/proceeds-report/bookings',
      proceedsReportDownload: 'organizations/:organizationId/org-admin/proceeds-report/download',
   },

   program: {
      getAll: 'programs',
      getById: 'programs/:id',
      create: 'programs',
      update: 'programs/:id',
      delete: 'programs/:id',
      reorder: 'programs/reorder',
   },

   schedule: {
      getAll: 'schedules',
      getById: 'schedules/:id',
      preflight: 'schedules/upload/preflight',
      preflightText: 'schedules/upload/preflight-text',
      commit: 'schedules/upload/commit',
      update: 'schedules/:id',
      delete: 'schedules/:id',
      bulkGames: 'schedules/:id/games',
      appendGames: 'schedules/:id/games/import',
      updateGame: 'schedules/games/:gameId',
      opponents: 'schedules/opponents',
      tvNetworks: 'schedules/tv-networks',
      opponentById: 'schedules/opponents/:opponentId',
      tvNetworkById: 'schedules/tv-networks/:networkId',
      enrichOpponent: 'schedules/opponents/:opponentId/enrich',
      enrichTvNetwork: 'schedules/tv-networks/:networkId/enrich',
      enrichAllOpponents: 'schedules/opponents/enrich-all',
      enrichAllTvNetworks: 'schedules/tv-networks/enrich-all',
      confirmOpponentLogo: 'schedules/opponents/:opponentId/confirm-logo',
      confirmTvNetworkLogo: 'schedules/tv-networks/:networkId/confirm-logo',
      // NPT-1555: club coverage grid + bulk creation
      coverage: 'organizations/:organizationId/schedule-coverage',
      // Every organization the caller may see, paginated + filtered + summarised on the server
      coveragePage: 'schedules/coverage',
      // NPT-1645: every destination for an org + its chapters, as map pins
      destinations: 'organizations/:organizationId/destinations',
      bulkCreate: 'schedules/bulk-create',
      bulkPreflight: 'schedules/bulk-create/preflight',
      // NPT-1556: per-game hotel tier designations
      gameHotelDesignations: 'schedules/games/:gameId/hotel-designations',
      gameHotelDesignationsSuggest: 'schedules/games/:gameId/hotel-designations/suggest',
      // Team hotel autopick: the backlog queue and its bulk apply.
      missingTeamHotel: 'schedules/games/missing-team-hotel',
      bulkApplyTeamHotels: 'schedules/hotel-designations/bulk-apply',
   },

   libraryEvent: {
      getAll: 'library-events',
      getById: 'library-events/:id',
      create: 'library-events',
      // NPT-1639: create many events from one pasted list
      bulkCreate: 'library-events/bulk',
      update: 'library-events/:id',
      delete: 'library-events/:id',
      attach: 'library-events/:id/attach',
      detach: 'library-events/:id/detach',
      dedupeCandidates: 'library-events/dedupe-candidates',
      merge: 'library-events/merge',
      hotelDesignations: 'library-events/:id/hotel-designations',
      hotelDesignationsSuggest: 'library-events/:id/hotel-designations/suggest',
   },

   gameGuide: {
      base: 'schedules/games/:gameId/guide',
      generate: 'schedules/games/:gameId/guide/generate',
      generateHero: 'schedules/games/:gameId/guide/generate-hero',
      confirmHero: 'schedules/games/:gameId/guide/confirm-hero',
      lookupPlace: 'schedules/games/:gameId/guide/lookup-place',
      restaurants: 'schedules/games/:gameId/guide/restaurants',
      thingsToDo: 'schedules/games/:gameId/guide/things-to-do',
      customEvents: 'schedules/games/:gameId/guide/custom-events',
      generateDefaults: 'schedules/:scheduleId/guide/generate-defaults',
      applyDefaults: 'schedules/:scheduleId/guide/apply-defaults',
      public: 'traveler/game-guide/:orgSlug/:programSlug/:gameId',
   },

   transaction: {
      getAll: 'transaction',
      export: 'transaction/download',
   },

   coupon: {
      getAll: 'coupon',
      getById: 'coupon/:id',
      create: 'coupon',
      update: 'coupon/:id',
      checkValid: 'coupon/isValid',
   },

   activityLog: {
      getAll: 'api-activity-logs/preview',
      export: 'api-activity-logs/download',
   },

   bookingFailureLog: {
      index: 'booking-failure-logs',
      getById: 'booking-failure-logs/:id',
      notes: 'booking-failure-logs/notes',
   },

   userOrganization: {
      userOrganizations: 'UserOrganizations',
   },

   userProgram: {
      upsert: 'user-program/:programId',
   },

   myTokens: {
      myTokens: 'authentication',
   },

   health: 'Health',

   files: {
      index: 'files',
      uploadImage: 'files/image',
      generateUploadPresignedUrl: 'files/generateUploadPresignedUrl',
   },

   booking: {
      cancel: 'booking/:partnerOrderId/cancel',
      confirm: 'booking/:partnerOrderId/confirmation-code',
      resendConfirmation: 'booking/:partnerOrderId/resend-confirmation',
      finalize: 'hotel/booking',
      form: 'hotel/book/form',
      getDetail: 'booking/:partnerOrderId',
      getMe: 'booking/me',
      index: 'booking',
      preBook: 'hotel/prebook',
      // NPT-1450 — admin booking
      assignUser: 'booking/:partnerOrderId/assign-user',
      resolveManualRefund: 'booking/:partnerOrderId/manual-refund/resolve',
      markInvoicePaid: 'booking/:partnerOrderId/invoice/mark-paid', // NPT-1492 gap, folded into NPT-1450
      // NPT-2080 — admin trip support panel
      support: 'booking/:partnerOrderId/support',
      supplierStatus: 'booking/:partnerOrderId/support/supplier-status',
      pathToBooking: 'booking/:partnerOrderId/support/path-to-booking',
      // NPT-2060 — self-serve modification
      modificationOptions: 'booking/:partnerOrderId/modification-options',
      modifyGuestNames: 'booking/:partnerOrderId/modify/guest-names',
      quoteDateChange: 'booking/:partnerOrderId/modify/dates/quote',
      confirmDateChange: 'booking/:partnerOrderId/modify/dates/confirm',
      searchStay: 'booking/:partnerOrderId/modify/stay/search',
      quoteStay: 'booking/:partnerOrderId/modify/stay/quote',
      confirmStay: 'booking/:partnerOrderId/modify/stay/confirm',
      modificationHistory: 'booking/:partnerOrderId/modifications',
      modificationsNeedingAttention: 'booking/modifications/attention',
      resolveModification: 'booking/modifications/:modificationId/resolve',
      // NPT-2210 — support request from a refused change
      supportRequest: 'booking/:partnerOrderId/support-request',
   },

   hotelBlacklist: {
      getAll: 'hotel-blacklist',
      create: 'hotel-blacklist',
      delete: 'hotel-blacklist',
   },

   // Admin-only endpoints, but the API namespace has no `/admin` prefix — admin access
   // is enforced by permission, not by URL (npt-api convention). Do not mirror
   // routePaths.ADMIN.FRAUD_WATCH here.
   fraudWatch: {
      index: 'fraud-watch',
      count: 'fraud-watch/count',
      getById: 'fraud-watch/:userId',
      action: 'fraud-watch/:userId/:action',
   },

   dispute: {
      getAll: 'dispute',
      review: 'dispute/:id/review',
      update: 'dispute/:id',
      evidencePack: 'dispute/:id/evidence-pack',
   },

   globalConfig: '/global-configs',
   destinations: {
      site: '/site/organizations/:organizationId/popular-destinations',
      traveler: '/traveler/organizations/:organizationId/popular-destinations',
      reorder: '/popular-destinations/reorder',
   },

   trendingDestinations: {
      site: '/site/organizations/:organizationId/trending-destinations',
      traveler: '/traveler/organizations/:organizationId/trending-destinations',
      reorder: '/trending-destinations/reorder',
      platformSite: '/platform/sites/:siteId/trending-destinations',
   },

   touristCenterOverrides: {
      list: '/platform/tourist-center-overrides',
   },

   recentSearch: {
      list: '/me/recent-searches',
      upsert: '/me/recent-searches',
      remove: '/me/recent-searches/:id',
   },

   site: {
      getSites: 'sites',
      getSite: 'sites/:id',
      update: 'sites/:id',
   },

   events: {
      site: '/events',
      traveler: '/traveler/organizations/:organizationId/upcoming-events',
      details: '/traveler/organizations/event-detail/:slug',
      travelerRSVP: '/traveler/organizations/event/action',
      rsvpEvent: '/traveler/organizations/rsvp-event',
   },

   fonts: {
      list: 'fonts',
      listAdmin: 'fonts/admin',
      base: 'fonts',
      byId: 'fonts/:id',
   },
};
